import {
  Chip,
  Description,
  FieldError,
  Fieldset,
  Form,
  Input,
  Label,
  Spinner,
  Tabs,
  TextField,
  useOverlayState,
} from '@heroui/react';
import { LockKeyhole, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { updateMe } from '../../../api/user';
import { PageHeader } from '../../../components/page/page-header';
import { AsyncButton } from '../../../components/ui/async-button';
import { primaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { drawerFieldClassName } from '../../../components/ui/side-drawer-classes';
import { applyMeResponseToStore, refreshUserDoc, useAuth } from '../../../hooks/useAuth';
import { refreshUser, updateAuthPhoto } from '../../../lib/firebase';
import { isPhoneVerifiedOnFirebase } from '../../../lib/firebase-phone';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { isDocumentRequired } from '../../../utils/profileValidation';
import { formatDocument, formatPhone } from '../../../utils/validators';
import { isPhoneVerificationSkipped } from '../../../utils/phone';
import { AccountSettingsPanel } from '../components/account-settings-panel';
import { PhoneVerificationModal } from '../components/phone-verification-modal';
import { ProfilePhotoField } from '../components/profile-photo-field';
import {
  emptyProfileForm,
  profileFormFromMe,
  validateProfileForm,
  type ProfileFormFields,
} from '../utils/profile-form';

type ProfileTab = 'profile' | 'account';

function resolveTab(value: string | null): ProfileTab {
  return value === 'account' ? 'account' : 'profile';
}

export default function EditProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { authUser, userDoc, loading: authLoading, setAuthUser } = useAuth();
  const verificationModal = useOverlayState();
  const requireDocument = isDocumentRequired();

  const [selectedTab, setSelectedTab] = useState<ProfileTab>(() =>
    resolveTab(searchParams.get('tab')),
  );
  const [form, setForm] = useState<ProfileFormFields>(emptyProfileForm);
  const [initialPhone, setInitialPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ProfileFormFields, string>>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedTab(resolveTab(searchParams.get('tab')));
  }, [searchParams]);

  useEffect(() => {
    if (authLoading) return;

    if (userDoc) {
      const next = profileFormFromMe({
        firstName: userDoc.firstName ?? userDoc.customer?.firstName,
        lastName: userDoc.lastName ?? userDoc.customer?.lastName,
        documentNumber: userDoc.customer?.documentNumber,
        phone: userDoc.customer?.phone,
      });
      setForm(next);
      setInitialPhone(next.phone ?? '');
      setPhoneVerified(isPhoneVerifiedOnFirebase(authUser, next.phone ?? ''));
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [authLoading, authUser, userDoc]);

  useEffect(() => {
    if (!attemptedSubmit) return;
    setFieldErrors(validateProfileForm(form));
  }, [form, attemptedSubmit]);

  const phoneChanged = useMemo(() => {
    const current = (form.phone ?? '').replace(/\D/g, '');
    const initial = initialPhone.replace(/\D/g, '');
    return current !== initial;
  }, [form.phone, initialPhone]);

  const needsPhoneVerification =
    !isPhoneVerificationSkipped() &&
    !!form.phone?.trim() &&
    (phoneChanged || !phoneVerified);

  const handleTabChange = (key: string) => {
    const next = resolveTab(key);
    setSelectedTab(next);
    setSearchParams(next === 'profile' ? {} : { tab: next }, { replace: true });
  };

  const handlePhoneChange = (phone: string) => {
    setForm((prev) => ({ ...prev, phone }));
    if (phoneVerified && phone.replace(/\D/g, '') !== initialPhone.replace(/\D/g, '')) {
      setPhoneVerified(false);
    }
  };

  const saveProfile = async () => {
    const updated = await updateMe({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone,
      documentNumber: form.documentNumber.trim() || undefined,
    });
    applyMeResponseToStore(updated);
    await refreshUserDoc();
    if (authUser) {
      const refreshed = await refreshUser(authUser);
      setAuthUser(refreshed);
      setPhoneVerified(isPhoneVerifiedOnFirebase(refreshed, form.phone ?? ''));
    } else {
      setPhoneVerified(isPhoneVerifiedOnFirebase(authUser, form.phone ?? ''));
    }
    setInitialPhone(form.phone ?? '');
    showSuccess('Perfil atualizado com sucesso.');
  };

  const handlePhotoChanged = async (photoUrl: string) => {
    if (authUser) {
      const withPhoto = await updateAuthPhoto(authUser, photoUrl);
      setAuthUser(withPhoto);
    }
    const updated = await updateMe({ photoUrl });
    applyMeResponseToStore(updated);
    showSuccess('Foto de perfil atualizada.');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    setAttemptedSubmit(true);
    const errors = validateProfileForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (needsPhoneVerification) {
      verificationModal.open();
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await saveProfile();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar perfil.';
      setError(message);
      showErrorFromUnknown(err, 'Erro ao atualizar perfil.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerified = async () => {
    if (authUser) {
      const refreshed = await refreshUser(authUser);
      setAuthUser(refreshed);
      setPhoneVerified(isPhoneVerifiedOnFirebase(refreshed, form.phone ?? ''));
    } else {
      setPhoneVerified(true);
    }
    setSubmitting(true);
    setError(null);
    try {
      await saveProfile();
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao atualizar perfil.');
      setError(err instanceof Error ? err.message : 'Erro ao atualizar perfil.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-6">
      <PageHeader
        title="Perfil e Conta"
        description={
          selectedTab === 'account'
            ? 'Gerencie o acesso da sua conta e a senha de login.'
            : 'Atualize seus dados pessoais.'
        }
      />

      <Tabs
        selectedKey={selectedTab}
        onSelectionChange={(key) => handleTabChange(String(key))}
      >
        <Tabs.ListContainer>
          <Tabs.List aria-label="Seções do perfil" className="w-full p-1.5">
            <Tabs.Tab id="profile" className={primaryPageTabClassName}>
              <span className="inline-flex items-center gap-1.5 sm:gap-2">
                <UserRound size={16} className="shrink-0 sm:size-[18px]" />
                Perfil
              </span>
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="account" className={primaryPageTabClassName}>
              <span className="inline-flex items-center gap-1.5 sm:gap-2">
                <LockKeyhole size={16} className="shrink-0 sm:size-[18px]" />
                Conta
              </span>
              <Tabs.Indicator />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>

        <Tabs.Panel id="profile" className="flex flex-col gap-4 pt-4">
          {error ? (
            <StatusCallout status="danger" title="Não foi possível salvar">
              {error}
            </StatusCallout>
          ) : null}

          <Form onSubmit={handleSubmit}>
            <Fieldset>
              <Fieldset.Legend>Dados pessoais</Fieldset.Legend>
              <Description>
                Atualize seus dados pessoais e valide seu telefone por SMS.
              </Description>
              <ProfilePhotoField
                name={
                  [form.firstName, form.lastName].filter(Boolean).join(' ') ||
                  userDoc?.displayName ||
                  authUser?.email ||
                  'Usuário'
                }
                photoUrl={userDoc?.photoURL ?? authUser?.photoURL ?? null}
                onChanged={handlePhotoChanged}
              />
              <Fieldset.Group className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <TextField
                  name="firstName"
                  fullWidth
                  isInvalid={attemptedSubmit && !!fieldErrors.firstName}
                >
                  <Label>Nome</Label>
                  <Input
                    fullWidth
                    className={drawerFieldClassName}
                    value={form.firstName}
                    onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    autoComplete="given-name"
                  />
                  {attemptedSubmit && fieldErrors.firstName ? (
                    <FieldError>{fieldErrors.firstName}</FieldError>
                  ) : null}
                </TextField>

                <TextField
                  name="lastName"
                  fullWidth
                  isInvalid={attemptedSubmit && !!fieldErrors.lastName}
                >
                  <Label>Sobrenome</Label>
                  <Input
                    fullWidth
                    className={drawerFieldClassName}
                    value={form.lastName}
                    onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    autoComplete="family-name"
                  />
                  {attemptedSubmit && fieldErrors.lastName ? (
                    <FieldError>{fieldErrors.lastName}</FieldError>
                  ) : null}
                </TextField>

                <TextField
                  name="documentNumber"
                  fullWidth
                  isInvalid={attemptedSubmit && !!fieldErrors.documentNumber}
                >
                  <Label>CPF{requireDocument ? '' : ' (opcional)'}</Label>
                  <Input
                    fullWidth
                    className={drawerFieldClassName}
                    value={form.documentNumber}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        documentNumber: formatDocument(e.target.value, 'CPF'),
                      }))
                    }
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    data-clarity-mask="true"
                  />
                  {!requireDocument ? (
                    <Description className="text-xs">Opcional em staging/local</Description>
                  ) : null}
                  {attemptedSubmit && fieldErrors.documentNumber ? (
                    <FieldError>{fieldErrors.documentNumber}</FieldError>
                  ) : null}
                </TextField>

                <TextField
                  name="phone"
                  fullWidth
                  isInvalid={attemptedSubmit && !!fieldErrors.phone}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Label>Telefone</Label>
                    {form.phone?.trim() ? (
                      <Chip
                        size="sm"
                        variant="soft"
                        color={needsPhoneVerification ? 'warning' : 'success'}
                      >
                        {needsPhoneVerification ? 'Verificação pendente' : 'Verificado'}
                      </Chip>
                    ) : null}
                  </div>
                  <Input
                    fullWidth
                    className={drawerFieldClassName}
                    value={form.phone ?? ''}
                    onChange={(e) => handlePhoneChange(formatPhone(e.target.value))}
                    placeholder="(11) 99999-9999"
                    inputMode="tel"
                    autoComplete="tel"
                    data-clarity-mask="true"
                  />
                  {attemptedSubmit && fieldErrors.phone ? (
                    <FieldError>{fieldErrors.phone}</FieldError>
                  ) : needsPhoneVerification ? (
                    <Description className="text-xs">
                      Ao salvar, enviaremos um código SMS para confirmar este número.
                    </Description>
                  ) : null}
                </TextField>
              </Fieldset.Group>
              <Fieldset.Actions>
                <AsyncButton type="submit" variant="primary" isPending={submitting}>
                  Salvar alterações
                </AsyncButton>
              </Fieldset.Actions>
            </Fieldset>
          </Form>
        </Tabs.Panel>

        <Tabs.Panel id="account" className="pt-4">
          <AccountSettingsPanel
            authUser={authUser}
            email={userDoc?.email ?? authUser?.email}
          />
        </Tabs.Panel>
      </Tabs>

      <PhoneVerificationModal
        state={verificationModal}
        phone={form.phone ?? ''}
        onVerified={handleVerified}
      />
    </div>
  );
}
